type PointerCoordinateEvent = {
    clientX: number
    clientY: number
}

type CompatibleClickSuppression = {
    until: number
    clientX: number
    clientY: number
    radiusPx: number
}

type WindowWithCompatibleClickSuppression = Window & {
    __fitroPenCompatibleClickSuppression?: CompatibleClickSuppression
    __fitroPenCompatibleClickSuppressInstalled?: boolean
}

const DEFAULT_COMPATIBLE_CLICK_SUPPRESSION_MS = 750
const DEFAULT_COMPATIBLE_CLICK_SUPPRESSION_RADIUS_PX = 18

function installCompatibleClickSuppressor() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return null

    const windowWithSuppression = window as WindowWithCompatibleClickSuppression
    if (windowWithSuppression.__fitroPenCompatibleClickSuppressInstalled) {
        return windowWithSuppression
    }

    windowWithSuppression.__fitroPenCompatibleClickSuppressInstalled = true

    // A new contact owns its own click, even if the previous device never
    // delivered the compatibility click we were waiting for.
    document.addEventListener('pointerdown', () => {
        windowWithSuppression.__fitroPenCompatibleClickSuppression = undefined
    }, true)

    document.addEventListener('click', (event) => {
        // Keyboard and assistive/programmatic activation are not the trailing
        // pointer click. Preserve both the activation and the pending guard.
        if (event.detail === 0) return

        const suppression = windowWithSuppression.__fitroPenCompatibleClickSuppression
        if (!suppression) return

        windowWithSuppression.__fitroPenCompatibleClickSuppression = undefined

        if (Date.now() > suppression.until) return

        const distance = Math.hypot(
            event.clientX - suppression.clientX,
            event.clientY - suppression.clientY
        )
        if (distance > suppression.radiusPx) return

        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
    }, true)

    return windowWithSuppression
}

export function suppressNextCompatibleClick(
    event: PointerCoordinateEvent,
    {
        timeoutMs = DEFAULT_COMPATIBLE_CLICK_SUPPRESSION_MS,
        radiusPx = DEFAULT_COMPATIBLE_CLICK_SUPPRESSION_RADIUS_PX,
    }: {timeoutMs?: number; radiusPx?: number} = {}
) {
    const windowWithSuppression = installCompatibleClickSuppressor()
    if (!windowWithSuppression) return

    const clientX = Number.isFinite(event.clientX) ? event.clientX : 0
    const clientY = Number.isFinite(event.clientY) ? event.clientY : 0

    windowWithSuppression.__fitroPenCompatibleClickSuppression = {
        until: Date.now() + timeoutMs,
        clientX,
        clientY,
        radiusPx,
    }
}
